import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import React from 'react';
import { applySpelling } from '@/utils/spelling';
import { useRegion } from '@/contexts/RegionContext';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Building2, ArrowRight, ShieldCheck, TrendingUp, Users, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Link } from 'react-router-dom';
import { useRegionalPageContent } from '@/hooks/useRegionalPageContent';
import { LazyImage } from '@/components/ui/LazyImage';

export function OrganizationsPage() {
  const { regionCode } = useRegion();
  const { links } = useSiteSettings();
  const { t } = useTranslation();
  const s = (text: string) => applySpelling(text, regionCode);
  const { content: customContent, loading: customLoading } = useRegionalPageContent('organizations');

  const headline = customContent?.title || "Equip your leadership with unprecedented insight.";
  const description = "Improve your organizational culture by moving beyond surface-level symptoms. The FIVE LENS framework provides the data-driven depth required to align executive teams, resolve systemic conflict, and build high-trust, high-performance environments.";

  const getRegionalBlock = () => {
    switch (regionCode) {
      case 'us':
        return {
          title: "Accelerating US Enterprise Growth",
          p1: "In the hyper-competitive US corporate environment, speed to market and agile execution are everything. Misaligned executive teams don't just cause interpersonal friction - they create operational drag that costs millions in lost momentum and missed opportunities.",
          p2: "FIVE LENS gives American enterprises the data-driven framework needed to rapidly scale high-trust cultures, optimize M&A integrations, and maximize leadership ROI. Move beyond generic team building and implement precise interventions that drive aggressive organizational growth."
        };
      case 'za':
        return {
          title: "Resilient Leadership for South Africa",
          p1: "Operating in South Africa requires a unique breed of resilient leadership. We deeply understand the complexities of transformation, workplace diversity, and the necessity of navigating emerging market volatility with emotional intelligence.",
          p2: "FIVE LENS helps South African organizations build deeply connected teams, fostering a culture of genuine inclusivity, psychological safety, and agile decision-making. We provide the tools to turn diverse perspectives into your organization's greatest strategic asset."
        };
      case 'gb':
      case 'uk':
        return {
          title: "Navigating UK Corporate Complexity",
          p1: "For UK organizations navigating shifting economic frameworks and traditional governance structures, sustainable productivity and strategic alignment are paramount. Traditional personality typing often fails to penetrate entrenched corporate silos.",
          p2: "FIVE LENS provides British enterprises with a measured, scientifically rigorous approach to change management. Our multidimensional framework breaks down barriers, fosters collaborative agility across global divisions, and ensures your leadership pipeline is equipped for modern complexities."
        };
      default:
        return {
          title: "The limitations of standard assessments",
          p1: "In today's complex business landscape, traditional personality assessments fall short. They categorize individuals into neat boxes without addressing the underlying developmental maturity, emotional agility, and social drives that dictate how teams truly operate under pressure.",
          p2: "When leaders are placed in high-stress, rapidly shifting environments, simple typology fails to predict behavior. The FIVE LENS model provides a unified framework to measure these dynamic shifts, allowing you to implement targeted interventions that drive sustainable behavioral change."
        };
    }
  };

  const regionalBlock = getRegionalBlock();

  return (
    <main className="pt-32 pb-24 min-h-screen bg-ground">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <Breadcrumb items={[
          { label: t('nav.organizations', 'For Organizations & HR Teams') }
        ]} />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="max-w-3xl">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-[1px] w-8 bg-rule-3"></span>
              <span className="text-[11px] uppercase tracking-[0.3em] font-bold text-rule-3">
                {t('orgs.eyebrow', 'Enterprise Solutions')}
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight mb-8 text-ink font-normal">
              {headline}
            </h1>
            
            {customContent?.htmlContent ? (
              <div 
                className="prose prose-brand max-w-none prose-p:text-xl prose-p:font-light prose-p:text-ink/70/80 prose-p:mb-12 prose-p:leading-relaxed"
                dangerouslySetInnerHTML={{ __html: customContent.htmlContent }}
              />
            ) : (
              <p className="text-xl font-light text-ink/70/80 mb-12 leading-relaxed">
                {description}
              </p>
            )}
          </div>

          <div className="mb-24 w-full h-[500px] overflow-hidden bg-ground-deep relative">
            <div className="absolute inset-0 bg-ink/20 mix-blend-multiply z-10" />
            <LazyImage 
              src="/five_lens_enterprise_solutions_web.webp"
              alt="Diverse executive team collaborating in a bright modern office"
              className="w-full h-full object-cover relative z-0"
              referrerPolicy="no-referrer"
            />
          </div>

          <div className="grid lg:grid-cols-12 gap-16 mb-24">
            <div className="lg:col-span-4">
              <h2 className="text-3xl font-serif text-ink mb-6 leading-tight">
                {regionalBlock.title}
              </h2>
              <div className="h-1 w-12 bg-rule-3 mb-6"></div>
            </div>
            <div className="lg:col-span-8 prose prose-lg prose-brand max-w-none text-ink/70/80 leading-relaxed font-light">
              <p className="mb-6">
                {regionalBlock.p1}
              </p>
              <p>
                {regionalBlock.p2}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-24">
            <div className="bg-white p-10 border border-ground-deep hover:border-rule-3 transition-colors group">
              <ShieldCheck className="w-10 h-10 text-rule-3 mb-6 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <h3 className="text-xl font-serif font-bold text-ink mb-4">Culture Development</h3>
              <p className="text-ink/70/70 leading-relaxed font-light mb-6">
                Rebuild psychological safety and trust by addressing the root causes of miscommunication and protective behaviors.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-1 flex-shrink-0" /> Conflict resolution mapping</li>
                <li className="flex items-start gap-3 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-1 flex-shrink-0" /> Team trust rebuilding</li>
                <li className="flex items-start gap-3 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-1 flex-shrink-0" /> Psychological safety baselines</li>
              </ul>
            </div>
            
            <div className="bg-white p-10 border border-ground-deep hover:border-rule-3 transition-colors group transform md:-translate-y-4">
              <TrendingUp className="w-10 h-10 text-rule-3 mb-6 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <h3 className="text-xl font-serif font-bold text-ink mb-4">Leadership Maturity</h3>
              <p className="text-ink/70/70 leading-relaxed font-light mb-6">
                Measure and develop your leaders' capacity to handle complexity, ambiguity, and rapid organizational change.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-1 flex-shrink-0" /> Vertical development tracks</li>
                <li className="flex items-start gap-3 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-1 flex-shrink-0" /> Executive succession planning</li>
                <li className="flex items-start gap-3 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-1 flex-shrink-0" /> Emotional resilience coaching</li>
              </ul>
            </div>
            
            <div className="bg-white p-10 border border-ground-deep hover:border-rule-3 transition-colors group">
              <Users className="w-10 h-10 text-rule-3 mb-6 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <h3 className="text-xl font-serif font-bold text-ink mb-4">Assessments at Scale</h3>
              <p className="text-ink/70/70 leading-relaxed font-light mb-6">
                Deploy our comprehensive diagnostic platform across departments for a unified, enterprise-wide development language.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-1 flex-shrink-0" /> Unlimited organizational reports</li>
                <li className="flex items-start gap-3 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-1 flex-shrink-0" /> Secure practitioner portals</li>
                <li className="flex items-start gap-3 text-sm text-ink/70/80"><CheckCircle2 className="w-4 h-4 text-rule-3 mt-1 flex-shrink-0" /> Custom cohort analytics</li>
              </ul>
            </div>
          </div>

          <div className="mb-24">
            <h2 className="text-3xl font-serif text-ink mb-12 text-center">The FIVE LENS Advantage</h2>
            <div className="grid md:grid-cols-2 gap-12">
              <div className="bg-white p-8 border border-ground-deep">
                <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-4">Actionable Data Over Typology</h4>
                <p className="text-sm text-ink/70/80 leading-relaxed">
                  Unlike traditional personality tests that box employees into static categories, our framework measures how individuals and teams shift behavior under stress. We provide actionable, dynamic data that reveals <em>why</em> teams break down and exactly <em>how</em> to rebuild them.
                </p>
              </div>
              <div className="bg-white p-8 border border-ground-deep">
                <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-4">Enterprise-Wide Common Language</h4>
                <p className="text-sm text-ink/70/80 leading-relaxed">
                  Siloed departments and post-merger integrations often fail due to mismatched cultures. The FIVE LENS provides a single, unified developmental vocabulary across your entire organization, breaking down barriers between leadership, sales, and operations.
                </p>
              </div>
              <div className="bg-white p-8 border border-ground-deep">
                <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-4">Scalable Leadership Pipeline</h4>
                <p className="text-sm text-ink/70/80 leading-relaxed">
                  Identify high-potential talent early. By measuring Personal Mastery and Emotional Resilience, you can track which emerging leaders have the psychological maturity and emotional agility required to succeed in senior executive roles.
                </p>
              </div>
              <div className="bg-white p-8 border border-ground-deep">
                <h4 className="font-bold text-rule-3 uppercase tracking-widest text-xs mb-4">Measurable ROI on Culture</h4>
                <p className="text-sm text-ink/70/80 leading-relaxed">
                  {s("Culture is no longer a soft metric. Organizations using the FIVE LENS report accelerated conflict resolution, reduced key-staff turnover, and significant improvements in cross-functional collaboration and psychological safety scores.")}
                </p>
              </div>
            </div>
          </div>

          <div className="mb-24 border-t border-ground-deep pt-24">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl font-serif text-ink mb-6">Common Enterprise Use Cases</h2>
              <p className="text-lg font-light text-ink/70/80">
                {s("Organizations deploy the FIVE LENS framework to solve complex, systemic human capital challenges that traditional coaching models fail to address.")}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex gap-6">
                <div className="w-12 h-12 bg-ground-deep/30 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-rule-3" />
                </div>
                <div>
                  <h4 className="font-bold text-ink mb-2">Mergers & Acquisitions</h4>
                  <p className="text-sm text-ink/70/70">{s("M&A failures are rarely financial; they are cultural. We map the cultural DNA of both organizations to preemptively identify friction points and integrate teams with minimal disruption.")}</p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="w-12 h-12 bg-ground-deep/30 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-5 h-5 text-rule-3" />
                </div>
                <div>
                  <h4 className="font-bold text-ink mb-2">Executive Team Alignment</h4>
                  <p className="text-sm text-ink/70/70">{s("When the C-suite is siloed, the entire organization suffers. We reveal the unconscious drivers causing executive friction and establish a framework for robust, trust-based debate.")}</p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="w-12 h-12 bg-ground-deep/30 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-rule-3" />
                </div>
                <div>
                  <h4 className="font-bold text-ink mb-2">High-Turnover Interventions</h4>
                  <p className="text-sm text-ink/70/70">Identify if high attrition is caused by a lack of psychological safety, unmet social drives, or poor emotional resilience at the management layer, allowing for targeted retention strategies.</p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="w-12 h-12 bg-ground-deep/30 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-rule-3" />
                </div>
                <div>
                  <h4 className="font-bold text-ink mb-2">Fast-Scaling & Hyper-Growth</h4>
                  <p className="text-sm text-ink/70/70">{s("Rapid growth places immense strain on organizational culture. We help startups build mature leadership pipelines and maintain their core cultural identity as they scale.")}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-24 border-t border-ground-deep pt-24">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl font-serif text-ink mb-6">How We Partner With You</h2>
              <p className="text-lg font-light text-ink/70/80">
                {s("We do not just drop a tool into your HR department. We partner with your executive and talent teams to seamlessly integrate the framework into your existing organizational strategy.")}
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 relative">
              {/* Connecting Line */}
              <div className="hidden md:block absolute top-1/2 left-0 w-full h-[1px] bg-ground-deep -z-10 -translate-y-1/2"></div>
              
              <div className="bg-ground p-8 text-center">
                <div className="w-12 h-12 bg-rule-3 text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-6 shadow-lg">1</div>
                <h4 className="font-serif font-bold text-ink text-xl mb-4">Discovery & Baseline</h4>
                <p className="text-sm text-ink/70/70">
                  {s("We begin by assessing the current state of your organizational culture, identifying systemic blockages, and defining clear, measurable developmental objectives for your teams.")}
                </p>
              </div>
              <div className="bg-ground p-8 text-center">
                <div className="w-12 h-12 bg-rule-3 text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-6 shadow-lg">2</div>
                <h4 className="font-serif font-bold text-ink text-xl mb-4">Assessment & Mapping</h4>
                <p className="text-sm text-ink/70/70">
                  {s("Your teams complete the comprehensive FIVE LENS diagnostic. We generate detailed individual, team, and organizational reports mapping behavioral dynamics, resilience, and maturity.")}
                </p>
              </div>
              <div className="bg-ground p-8 text-center">
                <div className="w-12 h-12 bg-rule-3 text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-6 shadow-lg">3</div>
                <h4 className="font-serif font-bold text-ink text-xl mb-4">Targeted Integration</h4>
                <p className="text-sm text-ink/70/70">
                  Through expert-led executive coaching, customized team alignment workshops, and practitioner training, we embed these insights directly into your day-to-day operations.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-ink via-ink/80 to-ink/90 text-ground p-10 md:p-16 relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="relative z-10 max-w-2xl">
              <h2 className="text-3xl md:text-4xl font-serif mb-4 text-white">Ready to improve your team?</h2>
              <p className="text-ground/80 text-lg mb-8 max-w-xl font-light">
                {s("Partner with our senior consultants to integrate the FIVE LENS framework into your organization’s talent strategy. Let\'s build a culture of high-performance and deep trust.")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href={links.bookingUrl}
                  className="inline-flex items-center justify-center bg-rule-3 text-white px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-white hover:text-ink transition-colors"
                >
                  Book a Strategy Call
                </a>
                <Link 
                  to="/team-effectiveness"
                  className="inline-flex items-center justify-center border border-white text-white px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-white/10 transition-colors"
                >
                  View Team Workshops
                </Link>
              </div>
            </div>
            <Building2 className="absolute -bottom-8 -right-8 w-80 h-80 text-white/5 pointer-events-none" />
          </div>
        </motion.div>
      </div>
    </main>
  );
}
